import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ContractInsightsData, OverdueSale } from '@/pages/dashboard/ContractInsights';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { ListPagination } from '@/components/ListPagination';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CollectionActionSheet, CollectionActionType } from '@/components/contracts/CollectionActionSheet';
import { Search, ChevronDown, ChevronRight, MoreHorizontal, ShieldCheck, StickyNote } from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import { businessToday, parseBusinessDay } from '@/lib/format';

interface Props {
    data: ContractInsightsData;
}

const PAGE_SIZE = 15;

interface InterestWeek {
    weekStart: Date;
    weekEnd: Date;
    expected: number;
    totalPaid: number;
    totalInterest: number;
    note: string | null;
    salesWithInterest: OverdueSale[];
}

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

/**
 * Below half a pesewa is float residue, not money. 0.005 is the engine's OWN dust constant — the
 * `if (finalAmt < 0.005) finalAmt = 0` residue guard in buildLoanPeriod [D-T1 rule 3], carried
 * verbatim by the in-database engine. Display only: no stored or computed value changes, an
 * amount this small simply stops printing as a signed, coloured, struck-through "-0.00".
 */
const DUST = 0.005;
const withoutDust = (amount: number) => (Math.abs(amount) < DUST ? 0 : amount);

/**
 * Formats overdue interest for display
 */
const formatInterest = (
    interest: number | null,
    applyOverdueInterest: boolean,
    surplusOverdueInterest: number | null
): React.ReactNode => {
    if (interest === null) {
        return <span className="text-muted-foreground">-</span>;
    }

    const elements: React.ReactNode[] = [];
    const shown = withoutDust(interest);
    const shownSurplus = surplusOverdueInterest === null ? null : withoutDust(surplusOverdueInterest);

    // If there's surplus, show it first (green, above the main value)
    if (shownSurplus !== null) {
        const surplusDisplay = shownSurplus === 0
            ? '0.00'
            : `+${shownSurplus.toFixed(2)}`;
        elements.push(
            <div key="surplus" className={cn(
                shownSurplus !== 0 && "text-green-600",
            )}>
                {surplusDisplay}
            </div>
        );
    }

    // Show the main interest value
    const isNegative = shown < 0;
    const displayValue = shown === 0
        ? '0.00'
        : isNegative
            ? shown.toFixed(2)
            : `+${shown.toFixed(2)}`;
    const colorClass = shown === 0
        ? ''
        : isNegative
            ? 'text-red-600'
            : 'text-green-600';

    // Waiver styling only where there is an amount to strike through. A waived sale carrying real
    // interest keeps its strike; a cell showing 0.00 has no waived amount to report, so striking
    // it would state a waiver over nothing.
    const waived = !applyOverdueInterest && shownSurplus === null && shown !== 0;

    elements.push(
        <div key="interest" className={cn(colorClass, waived && "line-through")}>
            {displayValue}
        </div>
    );

    return <div className="flex flex-col items-start">{elements}</div>;
};

/**
 * Formats the total interest for a week
 */
const formatWeekTotalInterest = (salesWithInterest: OverdueSale[]): React.ReactNode => {
    if (!salesWithInterest.length) {
        return <span className="text-muted-foreground">-</span>;
    }

    let totalInterest = 0;
    let hasAnyInterest = false;
    let hasAnyNegativeInterest = false;
    let allNegativeWaived = true;

    for (const sale of salesWithInterest) {
        if (sale.overdue_interest !== null) {
            hasAnyInterest = true;
            // Same dust floor as the cells below: a residue sale must not tip this week's total
            // into "negative and entirely waived" and strike a figure that is real money.
            const interest = withoutDust(sale.overdue_interest);
            totalInterest += interest;
            if (interest < 0) {
                hasAnyNegativeInterest = true;
                if (sale.apply_overdue_interest) {
                    allNegativeWaived = false;
                }
            }
        }
    }

    if (!hasAnyInterest) {
        return <span className="text-muted-foreground">-</span>;
    }

    const isNegative = totalInterest < 0;
    const displayValue = totalInterest === 0
        ? '0.00'
        : isNegative
            ? totalInterest.toFixed(2)
            : `+${totalInterest.toFixed(2)}`;

    const colorClass = totalInterest === 0
        ? ''
        : isNegative
            ? 'text-red-600'
            : 'text-green-600';

    return (
        <span className={cn(colorClass, hasAnyNegativeInterest && allNegativeWaived && "line-through")}>
            {displayValue}
        </span>
    );
};

export default function ContractInsightInterest({ data }: Props) {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
    const debouncedSearch = useDebounce(searchTerm, 300);

    const toggleWeekExpanded = (weekKey: string) => {
        setExpandedWeeks(prev => {
            const next = new Set(prev);
            if (next.has(weekKey)) {
                next.delete(weekKey);
            } else {
                next.add(weekKey);
            }
            return next;
        });
    };

    const contractId = data.contract?.id;

    // Reset to page 1 when search changes
    useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);

    // Fetch waivers for this contract. The interest arithmetic already accounts for them
    // server-side; this read exists for the badges and the edit sheet.
    const { data: waivers } = useQuery({
        queryKey: ['contract-interest-waivers', contractId],
        queryFn: async () => {
            if (!contractId) return [];

            const { data, error } = await supabase
                .from('repayment_interest_waivers')
                .select('id, contract_id, period_start, period_end, reason')
                .eq('contract_id', contractId);

            if (error) throw error;
            return (data || []) as { id: number; contract_id: number; period_start: string; period_end: string; reason: string | null }[];
        },
        enabled: !!contractId,
    });

    // Fetch notes for this contract
    const { data: notes } = useQuery({
        queryKey: ['contract-interest-notes', contractId],
        queryFn: async () => {
            if (!contractId) return [];

            const { data, error } = await supabase
                .from('repayment_notes')
                .select('id, contract_id, period_start, period_end, reason')
                .eq('contract_id', contractId);

            if (error) throw error;
            return (data || []) as { id: number; contract_id: number; period_start: string; period_end: string; reason: string | null }[];
        },
        enabled: !!contractId,
    });

    // Sheet state for waiver/note actions
    const [sheetData, setSheetData] = useState<{
        type: CollectionActionType;
        existingRecord: {
            id: number;
            contract_id: number;
            period_start: string;
            period_end: string;
            reason: string | null;
        } | null;
        defaultPeriod: { from: Date; to: Date };
    } | null>(null);

    const sheetOpen = sheetData !== null;

    // Helper to find existing waiver/note for a week
    const getWaiverForWeek = (weekStart: Date, weekEnd: Date) => {
        const startStr = format(weekStart, 'yyyy-MM-dd');
        const endStr = format(weekEnd, 'yyyy-MM-dd');
        return waivers?.find(w =>
            startStr <= w.period_end &&
            endStr >= w.period_start
        );
    };

    const getNoteForWeek = (weekStart: Date, weekEnd: Date) => {
        const startStr = format(weekStart, 'yyyy-MM-dd');
        const endStr = format(weekEnd, 'yyyy-MM-dd');
        return notes?.find(n =>
            startStr <= n.period_end &&
            endStr >= n.period_start
        );
    };

    // Open sheet handler
    const openSheet = (
        type: CollectionActionType,
        existing?: { id: number; contract_id: number; period_start: string; period_end: string; reason: string | null } | null,
        weekDates?: { weekStart: Date; weekEnd: Date }
    ) => {
        setSheetData({
            type,
            existingRecord: existing || null,
            // Both branches emit calendar-day Dates, which is what CollectionActionSheet's
            // startOfWeek/endOfWeek and its 'yyyy-MM-dd' save contract expect. `new Date()` was not
            // one: west of Greenwich it stands for the viewer's yesterday in Ghana terms.
            defaultPeriod: weekDates ? { from: weekDates.weekStart, to: weekDates.weekEnd } : { from: businessToday(), to: businessToday() },
        });
    };

    const closeSheet = () => setSheetData(null);


    // The weeks the overdue engine priced, exactly as it grouped them. `monday` and `paid_at` are
    // 'yyyy-MM-dd' Ghana calendar days, so they enter through parseISO — a local-midnight Date,
    // which is the contract every date-fns call below reads. `new Date('2026-08-03')` would be UTC
    // midnight instead, i.e. the previous day west of Greenwich, and would shift week labels and
    // the note pairing for those viewers.
    const interestSchedule = useMemo((): InterestWeek[] => {
        const weeks: InterestWeek[] = (data.overdue?.weeks ?? [])
            // Only weeks that priced something. `total_interest` is the server's sum over ALL the
            // week's sales, waived ones included — the same sum this filter used client-side. Half
            // a pesewa is the floor, not exact zero: the engine computes on true doubles, so a week
            // that priced nothing lands on ±1e-13 rather than 0, and an exact test printed it as a
            // red, struck "-0.00" row. 0.005 is the engine's own dust constant [D-T1 rule 3].
            .filter(w => Math.abs(w.total_interest) >= DUST)
            .map(w => {
                const weekStart = parseISO(w.monday);
                return {
                    weekStart,
                    weekEnd: addDays(weekStart, 6),
                    expected: w.expected,
                    totalPaid: w.total_paid,
                    totalInterest: w.total_interest,
                    note: null as string | null,
                    salesWithInterest: w.sales,
                };
            });

        // Add notes to weeks
        // `period_start`/`period_end` are `date` columns, so `new Date('2026-08-03')` is UTC
        // midnight — an hour LATER than the same day's `weekStart` east of Greenwich, which drops
        // the note off its own week. parseBusinessDay puts both sides on local midnight.
        for (const note of data.repayment_notes || []) {
            const noteStart = parseBusinessDay(note.period_start);
            const noteEnd = parseBusinessDay(note.period_end);
            if (!noteStart || !noteEnd) continue;
            for (const week of weeks) {
                if (week.weekStart >= noteStart && week.weekStart <= noteEnd) {
                    week.note = note.reason;
                }
            }
        }

        // Most recent first
        return weeks.sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());
    }, [data.overdue, data.repayment_notes]);

    // Filter by search term
    const filteredSchedule = useMemo(() => {
        if (!debouncedSearch) return interestSchedule;

        const searchLower = debouncedSearch.toLowerCase();
        return interestSchedule.filter(week =>
            week.salesWithInterest.some(s =>
                s.description?.toLowerCase().includes(searchLower) ||
                s.remarks?.toLowerCase().includes(searchLower)
            ) ||
            week.note?.toLowerCase().includes(searchLower)
        );
    }, [interestSchedule, debouncedSearch]);

    // Pagination
    const totalCount = filteredSchedule.length;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const paginatedSchedule = filteredSchedule.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    // Stats for Interest tab. No fallback rate: contracts.overdue_interest_rate is NOT NULL in the
    // database, so an absent value means the payload is stale or partial. The card renders the
    // absent state — the same '-' the Interest Start Date card uses — instead of showing a daily
    // rate derived from a number nobody set.
    const interestRate = data.contract?.overdue_interest_rate;
    const dailyInterestRate = typeof interestRate === 'number' && Number.isFinite(interestRate)
        ? `${(interestRate / 365).toFixed(2)}%`
        : '-';
    // Interest starts on the Monday after the first full payment week
    const firstFullPaymentWeek = data.overdue?.first_full_payment_week;
    const interestStartDate = firstFullPaymentWeek
        ? addDays(parseISO(firstFullPaymentWeek), 7)
        : null;
    const waiversCount = waivers?.length ?? 0;

    // Calculate surplus recovered (sum of all positive interest values where it was applied to
    // service debt). Over EVERY priced sale, not just the weeks the table shows: a zero-interest
    // week can still hold a sale that recovered surplus.
    const surplusRecovered = (data.overdue?.weeks ?? []).flatMap(w => w.sales).reduce((sum, sale) => {
        if (sale.surplus_overdue_interest !== null && sale.surplus_overdue_interest > 0) {
            return sum + sale.surplus_overdue_interest;
        }
        if (sale.overdue_interest !== null && sale.overdue_interest > 0 && sale.apply_overdue_interest) {
            return sum + sale.overdue_interest;
        }
        return sum;
    }, 0);

    return (
        <div className="space-y-6">
            {/* Interest Summary Cards */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Overdue Interest"
                    value={<CurrencyDisplay amount={data.overdue_total} />}
                    description="The total accumulated interest from weeks where payments were less than the expected amount. This is recalculated based on all payment history and waivers."
                />
                <StatCard
                    title="Daily Interest Rate"
                    value={dailyInterestRate}
                    description="This rate is applied daily to any outstanding balance when payments fall short of the expected weekly amount."
                />
                <StatCard
                    title="Interest Start Date"
                    value={interestStartDate ? format(interestStartDate, 'MMM d, yyyy') : '-'}
                    description="The date when overdue interest begins to accrue. This is the Monday after the first week where the full expected payment was made."
                />
                <StatCard
                    title="Waivers Applied"
                    value={waiversCount}
                    description="The number of interest waiver periods applied to this contract. During waived periods, overdue interest is not charged even if payments fall short."
                />
                <StatCard
                    title="Surplus Recovered"
                    value={<CurrencyDisplay amount={surplusRecovered} />}
                    description="When overpayments occur after a shortfall, the surplus is used to offset previously accrued interest. This shows the total interest recovered from surplus payments."
                />
            </div>

            {/* Interest Table */}
            <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {totalPages > 0 && (
                        <ListPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                        />
                    )}
                </div>

                {/* No loading branch: the weeks arrive with `data`, which the page has already
                    resolved before this tab renders. */}
                {paginatedSchedule.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Week</TableHead>
                                <TableHead>Expected</TableHead>
                                <TableHead>Paid</TableHead>
                                <TableHead>Interest</TableHead>
                                <TableHead className="w-[200px]">Note</TableHead>
                                <TableHead className="w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedSchedule.map((week) => {
                                const weekKey = week.weekStart.toISOString();
                                const isExpanded = expandedWeeks.has(weekKey);
                                const hasMultipleSales = week.salesWithInterest.length > 1;

                                return (
                                    <Fragment key={weekKey}>
                                        <TableRow
                                            className={cn(hasMultipleSales && "cursor-pointer hover:bg-muted/50")}
                                            onClick={() => hasMultipleSales && toggleWeekExpanded(weekKey)}
                                        >
                                            <TableCell className="text-muted-foreground whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    {hasMultipleSales && (
                                                        isExpanded
                                                            ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                            : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                    )}
                                                    {format(week.weekStart, 'MMM d')} - {format(week.weekEnd, 'MMM d, yyyy')}
                                                    {hasMultipleSales && (
                                                        <span className="text-xs text-muted-foreground">({week.salesWithInterest.length})</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {formatCurrency(week.expected)}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {formatCurrency(week.totalPaid)}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {formatWeekTotalInterest(week.salesWithInterest)}
                                            </TableCell>
                                            <TableCell className="max-w-[150px] truncate text-muted-foreground">
                                                {week.note || '-'}
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                {/* Only show actions for weeks with negative interest (penalties) */}
                                                {(() => {
                                                    // Sum interest the same way as formatWeekTotalInterest
                                                    const weekInterest = week.salesWithInterest.reduce((sum, s) => {
                                                        return sum + (s.overdue_interest ?? 0);
                                                    }, 0);
                                                    const hasNegativeInterest = weekInterest < 0;
                                                    const weekWaiver = getWaiverForWeek(week.weekStart, week.weekEnd);
                                                    const weekNote = getNoteForWeek(week.weekStart, week.weekEnd);
                                                    const hasWaiver = !!weekWaiver;
                                                    const hasNote = !!weekNote;

                                                    if (!hasNegativeInterest && !hasWaiver && !hasNote) return null;

                                                    return (
                                                        <div className="flex items-center gap-1">
                                                            {hasWaiver && (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="text-green-600 border-green-300 bg-green-50 cursor-pointer text-xs"
                                                                    onClick={() => openSheet('waiver', weekWaiver, week)}
                                                                >
                                                                    <ShieldCheck className="h-3 w-3" />
                                                                </Badge>
                                                            )}
                                                            {hasNote && (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="text-amber-600 border-amber-300 bg-amber-50 cursor-pointer text-xs"
                                                                    onClick={() => openSheet('note', weekNote, week)}
                                                                >
                                                                    <StickyNote className="h-3 w-3" />
                                                                </Badge>
                                                            )}
                                                            {hasNegativeInterest && (
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button variant="ghost" size="icon" className="h-6 w-6">
                                                                            <MoreHorizontal className="h-4 w-4" />
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end">
                                                                        <DropdownMenuItem
                                                                            onClick={() => openSheet('waiver', hasWaiver ? weekWaiver : undefined, week)}
                                                                        >
                                                                            <ShieldCheck className="h-4 w-4 mr-2" />
                                                                            {hasWaiver ? 'Edit Waiver' : 'Add Waiver'}
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem
                                                                            onClick={() => openSheet('note', hasNote ? weekNote : undefined, week)}
                                                                        >
                                                                            <StickyNote className="h-4 w-4 mr-2" />
                                                                            {hasNote ? 'Edit Note' : 'Add Note'}
                                                                        </DropdownMenuItem>
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </TableCell>
                                        </TableRow>
                                        {/* Expanded rows showing all sales */}
                                        {hasMultipleSales && isExpanded && week.salesWithInterest.map((sale) => {
                                            const isDummySale = sale.id === -1;
                                            return (
                                                <TableRow key={`${sale.id}-${sale.paid_at}`} className="bg-muted/30 border-0">
                                                    <TableCell className="pl-10 text-sm text-muted-foreground">
                                                        ↳ {format(parseISO(sale.paid_at), 'MMM d, h:mm a')}
                                                        {isDummySale && (
                                                            <span className="ml-2 text-xs text-orange-600">(Monday accrual)</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell className="text-sm text-muted-foreground">
                                                        {formatCurrency(sale.amount)}
                                                    </TableCell>
                                                    <TableCell className="text-sm">
                                                        {formatInterest(
                                                            sale.overdue_interest,
                                                            sale.apply_overdue_interest,
                                                            sale.surplus_overdue_interest
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                                                        {sale.description || '-'}
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <p className="text-center text-muted-foreground py-8">
                        {searchTerm ? 'No weeks match your search' : 'No interest entries to display'}
                    </p>
                )}
            </div>

            {/* Collection Action Sheet for waiver/note management */}
            {sheetData && contractId && (
                <CollectionActionSheet
                    open={sheetOpen}
                    onOpenChange={(open) => !open && closeSheet()}
                    type={sheetData.type}
                    contractId={contractId}
                    driverName={data.driver?.first_name && data.driver?.last_name
                        ? `${data.driver.first_name} ${data.driver.last_name}`
                        : 'Driver'}
                    defaultPeriod={sheetData.defaultPeriod}
                    existingRecord={sheetData.existingRecord}
                />
            )}
        </div>
    );
}
